import { collection, getDocs, query, where, orderBy, startAfter, limit, documentId, type QueryDocumentSnapshot, type DocumentData } from 'firebase/firestore';
import { db } from '@/integrations/firebase/client';
import type { AudienceInput } from '@/lib/personas';

export type PersonasSearchInput = AudienceInput & { limit?: number };

export type PersonaDoc = {
  id: string;
  name: string;
  age: number;
  gender?: string;
  location?: {
    country?: string;
    city?: string;
    environment?: string;
  };
  occupation?: string;
  estimated_income_eur?: number;
  primary_language?: string;
  technological_proficiency?: string;
  interests?: string[];
  subculture?: string[];
  preferred_platforms?: string[];
  description?: string;
  image?: string; // optional image URL
  filter_tag?: string; // optional tag for filtering
  order?: number; // sorting priority (higher first)
  // ... keep existing code (other potential fields from the collection)
};

export async function getAllPersonasCached(opts?: { force?: boolean }): Promise<PersonaDoc[]> {
  const CACHE_KEY = 'personas_cache_v1';
  const CACHE_TTL_MS = 8 * 60 * 60 * 1000; // 8 hours

  try {
    if (!opts?.force) {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { at: number; items: PersonaDoc[] };
        if (parsed && typeof parsed.at === 'number' && Array.isArray(parsed.items)) {
          if (Date.now() - parsed.at < CACHE_TTL_MS) {
            return parsed.items;
          }
        }
      }
    }
  } catch (e) {
    // Ignore cache read errors
  }

  // Fetch all personas from Firestore with pagination (no filters)
  const items = await fetchAllPersonasFromFirestore();

  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ at: Date.now(), items })
    );
  } catch (e) {
    // Ignore cache write errors
  }

  return items;
}

async function fetchAllPersonasFromFirestore(pageSize = 500): Promise<PersonaDoc[]> {
  const colRef = collection(db, 'personas');
  let results: PersonaDoc[] = [];
  let lastDoc: QueryDocumentSnapshot<DocumentData> | undefined = undefined;

  // Paginate by document ID to avoid composite index requirements
  while (true) {
    const q = lastDoc
      ? query(colRef, orderBy(documentId()), startAfter(lastDoc), limit(pageSize))
      : query(colRef, orderBy(documentId()), limit(pageSize));

    const snap = await getDocs(q);
    const batch = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as PersonaDoc[];
    results = results.concat(batch);

    if (snap.docs.length < pageSize) break;
    lastDoc = snap.docs[snap.docs.length - 1];
  }

  return results;
}

function normalize(str: string | undefined | null) {
  return (str ?? '').toString().trim().toLowerCase();
}

export async function searchPersonas(input: PersonasSearchInput): Promise<PersonaDoc[]> {
  const min = input.ageRange?.[0] ?? 0;
  const max = input.ageRange?.[1] ?? 100;

  // Load ALL personas from cache/Firestore without server-side filters
  const items = await getAllPersonasCached();

  const regions = (input.regions ?? []).map((r) => normalize(r)).filter(Boolean);
  const interests = (input.interests ?? []).map((i) => normalize(i)).filter(Boolean);

  const filtered = items.filter((it) => {
    // Age filter
    if (typeof it.age === 'number' && (it.age < min || it.age > max)) return false;

    // Region filtering by country or city if provided
    if (regions.length) {
      const country = normalize((it as any).location?.country ?? (it as any).country ?? (it as any).region);
      const city = normalize((it as any).location?.city);
      const matchRegion = regions.some((r) => r === country || r === city);
      if (!matchRegion) return false;
    }

    // Interests: at least one overlap
    if (interests.length) {
      const its: string[] = Array.isArray(it.interests) ? it.interests! : [];
      const itsNorm = its.map(normalize);
      const overlap = interests.some((i) => itsNorm.includes(i));
      if (!overlap) return false;
    }

    return true;
  });

  // Sort by 'order' descending, items without 'order' go last; tie-breaker by id
  return filtered.sort((a, b) => {
    const ao = a.order;
    const bo = b.order;
    if (typeof bo === 'number' && typeof ao === 'number') {
      if (bo !== ao) return bo - ao;
    } else if (typeof bo === 'number') {
      return -1; // b has order, comes first
    } else if (typeof ao === 'number') {
      return 1; // a has order, b doesn't
    }
    return a.id.localeCompare(b.id);
  });
}

